import { apiClient, type ApiClient } from '../http'
import type { IApiResponse } from '@/types'
import type {
  IStartChatSessionApiRequest,
  ISendChatMessageApiRequest,
  IChatSessionApiResponse,
} from './types'

export class ChatSessionApi {
  static async startChatSession(
    data: IStartChatSessionApiRequest,
    client: ApiClient = apiClient
  ): Promise<IChatSessionApiResponse> {
    return client.post(`/api/v1/ChatSession/start/${data.chatbotId}`, data)
  }

  static async getChatSession(
    sessionId: string,
    params?: { chatbotId?: string; tenantId?: string },
    client: ApiClient = apiClient
  ): Promise<IChatSessionApiResponse> {
    return client.get(`/api/v1/ChatSession/${sessionId}`, params)
  }

  static async sendMessage(
    data: ISendChatMessageApiRequest,
    onChunk: (_chunk: string) => void,
    onComplete?: () => void,
    onError?: (_error: Error) => void,
    client: ApiClient = apiClient
  ): Promise<void> {
    const baseUrl = client.getBaseUrl()
    const url = `${baseUrl}/api/v1/ChatSession/message/${data.chatbotId}`

    try {
      const authHeaders = await client.getAuthHeaders()

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...authHeaders,
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      if (!response.body) {
        throw new Error('Response body is null')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        const decoded = decoder.decode(value, { stream: true })
        buffer += decoded

        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.trim()) {
            const data = line.startsWith('data: ') ? line.substring(6) : line
            if (data.trim()) {
              onChunk(data)
            }
          }
        }
      }

      if (buffer.trim()) {
        const data = buffer.startsWith('data: ') ? buffer.substring(6) : buffer
        if (data.trim()) {
          onChunk(data)
        }
      }

      onComplete?.()
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      onError?.(err)
      throw err
    }
  }

  static async endChatSession(
    sessionId: string,
    client: ApiClient = apiClient
  ): Promise<IApiResponse<void>> {
    return client.delete<void>(`/api/v1/ChatSession/${sessionId}`)
  }
}

export const chatSessionApi = ChatSessionApi
export default ChatSessionApi
